import type { IDimension } from "types/configuration.type"
import type { TDimensions } from "types/modelNumbers.type"

/**
 * Parses a dimension string value which can be a single number ("10") or a range ("24-30").
 * Returns an array of numeric values extracted from the string.
 */
const parseDimensionValue = (value: string): number[] => {
	if (value.includes("-")) {
		const parts = value.split("-").map(Number).filter((n) => !isNaN(n))
		return parts
	}
	const num = Number(value)
	return isNaN(num) ? [] : [num]
}

/**
 * Parses an array of dimension strings and returns the min and max values.
 * Each string can be a single number ("10") or a range ("24-30").
 */
const getMinMax = (values: string[]): { min: number; max: number } => {
	const allNumbers = values.flatMap(parseDimensionValue)
	if (allNumbers.length === 0) return { min: 0, max: 0 }
	return {
		min: Math.min(...allNumbers),
		max: Math.max(...allNumbers),
	}
}

/**
 * Converts API model number dimensions (TDimensions) to the category dimensions format (IDimension).
 * TDimensions has width/height/depth as string arrays (e.g., ["10", "24-30"]).
 * IDimension has min_width, max_width, min_height, max_height, min_depth, max_depth as numbers.
 */
export const convertApiDimensionsToIDimension = (apiDimensions: TDimensions): IDimension => {
	const width = getMinMax(apiDimensions.width)
	const height = getMinMax(apiDimensions.height)
	const depth = getMinMax(apiDimensions.depth)

	return {
		min_width: width.min,
		max_width: width.max,
		min_height: height.min,
		max_height: height.max,
		min_depth: depth.min,
		max_depth: depth.max,
	}
}
